// frontend/src/admin/v2/DocViewer.jsx
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Stack, Box, Typography, Skeleton, Alert, IconButton,
  useTheme, useMediaQuery,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useDoc } from './hooks/useDocs'
import { SP, FS, hoverOnly } from '../../theme/tokens'
import Panel from '../../components/common/Panel'
import { formatBerlinDate } from '../../utils/time'

/**
 * DocViewer — /admin/docs/<topic>.
 *
 * @description
 *   Renders one runbook as HTML (server-side markdown → html). Sidebar
 *   shows the doc's TOC on desktop. Backlink at top + GitHub-source link
 *   at bottom for staff who prefer the raw markdown.
 */
const GITHUB_BASE =
  'https://github.com/BrewoutCoffeeBot/purchase-order-generator/blob/master/docs/knowledge'


function relTime(iso) {
  if (!iso) return null
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 30) return `${diffD}d ago`
  return formatBerlinDate(d)
}


export default function DocViewer() {
  const { topic } = useParams()
  const { data, loading, error } = useDoc(topic)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Stack spacing={SP.section}>
      {/* Back link */}
      <Stack direction="row" alignItems="center" spacing={SP.tight}>
        <IconButton component={RouterLink} to="/admin/docs" size="small" aria-label="Back to all docs">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          component={RouterLink}
          to="/admin/docs"
          variant="caption"
          sx={{
            fontSize: FS.xs, color: 'text.secondary',
            textDecoration: 'none', ...hoverOnly({ textDecoration: 'underline' }),
          }}
        >
          All docs
        </Typography>
      </Stack>

      {error && <Alert severity="error" variant="outlined">{error}</Alert>}

      {!data && loading && (
        <Stack spacing={SP.section}>
          <Skeleton variant="text" width="50%" height={48} />
          <Skeleton variant="rounded" height={400} />
        </Stack>
      )}

      {data && (
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={SP.section}
          alignItems="flex-start"
        >
          {/* TOC sidebar (desktop only) */}
          {!isMobile && data.toc_html && (
            <Panel
              pad="card"
              sx={{
                position: 'sticky',
                top: SP.section,
                width: 240,
                flexShrink: 0,
                maxHeight: 'calc(100vh - 200px)',
                overflowY: 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: FS.xs, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                  display: 'block', mb: SP.tight,
                }}
              >
                Contents
              </Typography>
              <Box
                className="doc-toc"
                sx={{
                  fontSize: FS.xs,
                  '& ul': { listStyle: 'none', pl: 1.5, m: 0 },
                  '& > .toc > ul': { pl: 0 },
                  '& li': { my: 0.25 },
                  '& a': { color: 'text.secondary', textDecoration: 'none' },
                  // K-5 (Welle 2): der Hover sitzt auf einem NACHFAHREN (`& a`),
                  // nicht auf der Flaeche selbst — `hoverOnly` setzt seinen
                  // inneren Schluessel fest auf den Hover-Selektor und kann
                  // diese Form nicht ausdruecken. Dieselbe Abfrage, ausgeschrieben.
                  '@media (hover: hover) and (pointer: fine)': {
                    '& a:hover': { color: 'primary.main', textDecoration: 'underline' },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: data.toc_html }}
              />
            </Panel>
          )}

          {/* Body */}
          <Stack spacing={SP.card} sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
            <Stack spacing={SP.tight}>
              <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }}>
                {data.title}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                {data.summary}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={SP.tight} flexWrap="wrap">
                <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.disabled' }}>
                  Modified {relTime(data.modified_at) || '—'}
                </Typography>
                <Typography
                  component="a"
                  href={`${GITHUB_BASE}/${data.path}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  sx={{
                    fontSize: FS.xs, color: 'primary.main',
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.25,
                    ...hoverOnly({ textDecoration: 'underline' }),
                  }}
                >
                  Edit on GitHub <OpenInNewIcon sx={{ fontSize: 11 }} />
                </Typography>
              </Stack>
            </Stack>

            <Panel sx={{ p: { xs: SP.card, md: SP.section } }}>
              <Box
                className="doc-body"
                sx={{
                  fontSize: FS.sm,
                  lineHeight: 1.65,
                  color: 'text.primary',
                  '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 0, mb: 1.5 },
                  '& h2': { fontSize: '1.2rem', fontWeight: 600, mt: 3, mb: 1.25, pb: 0.5, borderBottom: 1, borderColor: 'divider' },
                  '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 1 },
                  '& h4': { fontSize: '0.95rem', fontWeight: 600, mt: 1.5, mb: 0.75 },
                  '& p': { my: 1 },
                  '& ul, & ol': { pl: 3.5, my: 1 },
                  '& li': { my: 0.25 },
                  '& a': { color: 'primary.main', textDecoration: 'none' },
                  // K-5 (Welle 2): Nachfahren-Hover — `hoverOnly` kann diese
                  // Form nicht ausdruecken, dieselbe Zeigerabfrage ausgeschrieben.
                  '@media (hover: hover) and (pointer: fine)': {
                    '& a:hover': { textDecoration: 'underline' },
                  },
                  '& code': {
                    fontFamily: 'monospace',
                    fontSize: '0.85em',
                    bgcolor: 'action.hover',
                    px: 0.5, py: 0.125,
                    borderRadius: 0.5,
                  },
                  '& pre': {
                    bgcolor: 'action.hover',
                    p: SP.card,
                    borderRadius: 1,
                    overflowX: 'auto',
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                  },
                  '& pre code': {
                    bgcolor: 'transparent',
                    px: 0,
                    fontSize: 'inherit',
                  },
                  '& blockquote': {
                    borderLeft: 3,
                    borderColor: 'divider',
                    pl: 2,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    my: 1,
                  },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 1.5,
                    fontSize: '0.85rem',
                  },
                  '& th, & td': {
                    border: 1,
                    borderColor: 'divider',
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                  },
                  '& th': {
                    bgcolor: 'action.hover',
                    fontWeight: 600,
                  },
                  '& hr': {
                    border: 0,
                    borderTop: 1,
                    borderColor: 'divider',
                    my: 3,
                  },
                  '& img': { maxWidth: '100%', height: 'auto' },
                }}
                dangerouslySetInnerHTML={{ __html: data.html }}
              />
            </Panel>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}
